<template>
  <base-dialog @cancel="cancelDialog" width="1000px">
    <div slot="header">{{ params.isCreate ? $t('bill.text_142') : $t('bill.custom_rate') }}</div>
    <div slot="body">
      <template v-if="!params.isCreate">
        <a-alert class="mb-2" :message="$t('bill.rate_clone_tip')" />
        <dialog-selected-tips :count="params.data.length" :name="$t('bill.text_37')" :action="$t('bill.text_45')" />
        <dialog-table :data="params.data" :columns="params.columns.slice(0, 3)" />
      </template>
      <a-form-model
        ref="form"
        v-bind="formItemLayout"
        :model="form"
        :rules="rules">
        <a-form-model-item :label="$t('bill.rate.title.precision')" prop="precision">
          <a-select v-model="form.precision" @change="precisionChange">
            <a-select-option v-for="item in precisionOptions" :key="item.value" :value="item.value">
              {{item.text}}
            </a-select-option>
          </a-select>
        </a-form-model-item>
        <a-form-model-item :label="$t('bill.rate.title.price_type')" prop="price_type">
          <a-radio-group v-model="form.price_type" @change="priceTypeChange">
            <a-radio-button v-for="item in priceTypeOptions" :key="item.value" :value="item.value">
              {{ item.text }}
            </a-radio-button>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item :label="$t('bill.rate.title.rate')" class="mb-0">
          <price-step-rate :form="form" :rules="rules" />
        </a-form-model-item>
        <a-form-model-item :label="$t('bill.rate.start_month')" prop="effective_date">
          <a-month-picker v-model="form.effective_date" format="YYYY-MM" :allowClear="false" :placeholder="$t('common.tips.select', [$t('bill.rate.start_month')])" />
        </a-form-model-item>
      </a-form-model>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading">{{ $t('dialog.ok') }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import { int2Date } from '@Bill/utils'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'
import { validateModelForm } from '@/utils/validate'
import PriceStepRate from '../components/PriceStepRate'
import { getPriceStepAndUnit } from '../utils/columns'

export default {
  name: 'BillRateSetDialog',
  components: {
    PriceStepRate,
  },
  mixins: [DialogMixin, WindowsMixin],
  data () {
    const { isCreate, data } = this.params
    const initData = data[0]
    const { rate, price_unit, price_type, precision, cycle, unit: baseUnit, enable_date, step_rates = {}, usage_type, currency = 'CNY' } = initData
    const initRate = isCreate ? 0.0 : (Math.round(rate * 1000000) / 1000000).toFixed(6)
    const { step: initStep, unit } = getPriceStepAndUnit(price_unit)
    let initUnit = unit
    if (!initUnit) {
      if (usage_type === 'capacity' || usage_type === 'volume' || usage_type === 'traffic' || usage_type === 'traffic_tx' || usage_type === 'traffic_rx') {
        initUnit = 'GB'
      } else if (usage_type === 'bandwidth') {
        initUnit = 'Mbps'
      } else if (usage_type === 'instance') {
        initUnit = 'instance'
      } else {
        initUnit = baseUnit
      }
    }
    const initRangeList = []
    const keys = Object.keys(step_rates)
    keys.map((key, index) => {
      if (key === '_base_') {
        initRangeList.unshift({ line: 0, rate: step_rates[key] })
      } else {
        initRangeList.push({ line: Number(key), rate: step_rates[key] })
      }
    })
    return {
      loading: false,
      form: {
        precision: isCreate ? ((usage_type === 'traffic' || usage_type === 'traffic_tx' || usage_type === 'traffic_rx') ? 'usage' : 'second') : precision,
        price_type: isCreate ? 'amount' : price_type,
        cycle: isCreate ? 'day' : cycle,
        rate: initRate,
        effective_date: isCreate ? this.$moment() : this.$moment(int2Date(enable_date)),
        price_step: initStep,
        price_unit: initUnit,
        range_list: initRangeList.length ? initRangeList : [{ line: 0, rate: 0 }],
        usage_type,
        currency: isCreate ? 'CNY' : currency,
      },
      rules: {
        effective_date: { required: true, message: this.$t('common.tips.select', [this.$t('bill.rate.start_month')]) },
        price_step: { required: true, message: this.$t('common.tips.input', [this.$t('bill.rate.title.cycle')]) },
        item_line: { required: true, message: this.$t('common.tips.input', [this.$t('bill.rate.step_line')]) },
        rate: { required: true, message: this.$t('common.tips.input', [this.$t('bill.rate.title.rate')]) },
        item_rate: { required: true, message: this.$t('common.tips.input', [this.$t('bill.rate.title.rate')]) },
      },
      usage_type,
      priceTypeOptions: [
        { text: this.$t('rate_price_type.amount'), value: 'amount' },
        { text: this.$t('rate_price_type.amount_range'), value: 'amount_range' },
        { text: this.$t('rate_price_type.fixed_range'), value: 'fixed_range' },
      ],
      formItemLayout: {
        wrapperCol: {
          span: 21,
        },
        labelCol: {
          span: 3,
        },
      },
    }
  },
  computed: {
    precisionOptions () {
      if (this.usage_type === 'traffic' || this.usage_type === 'traffic_tx' || this.usage_type === 'traffic_rx') {
        return [
          { text: this.$t('rate_precision.usage'), value: 'usage' },
        ]
      } else {
        return [
          { text: this.$t('rate_precision.second'), value: 'second' },
          { text: this.$t('rate_precision.hour'), value: 'hour' },
          { text: this.$t('rate_precision.day'), value: 'day' },
          { text: this.$t('rate_precision.month'), value: 'month' },
          { text: this.$t('rate_precision.year'), value: 'year' },
        ]
      }
    },
  },
  created () {
    this.$r = new this.$Manager('rates', 'v1')
  },
  methods: {
    precisionChange (val) {
      if (val === 'month' || val === 'year') {
        this.form.cycle = 'month'
      } else {
        this.form.cycle = 'day'
      }
    },
    validateForm () {
      return new Promise((resolve, reject) => {
        this.form.fc.validateFields((err, values) => {
          if (!err) {
            resolve(values)
          } else {
            reject(err)
          }
        })
      })
    },
    async doCreate (params) {
      return this.$r.performAction({
        id: this.params.rate_id,
        data: params,
        action: 'add-rate',
      })
    },
    genParams () {
      const { precision, price_type, rate, price_step, price_unit, cycle, effective_date, range_list, currency } = this.form
      console.log('price_unit', price_unit)
      let price_unit_str = ''
      if (price_unit === undefined) {
        price_unit_str = ''
      } else {
        price_unit_str = price_unit
      }
      const params = {
        precision,
        price_type,
        cycle,
        enable_date: this.$moment(effective_date).startOf('month').format('YYYYMMDD'),
        rate_set_id: this.params.rate_set_id,
        price_unit: `${price_step}${price_unit_str}`,
        currency,
      }
      if (precision === 'usage') {
        params.cycle = 'usage'
      }
      if (price_type === 'amount') {
        params.rates = {
          _base_: Number(rate),
        }
      } else {
        const rates = {}
        range_list.map((item, index) => {
          if (index === 0) {
            rates._base_ = item.rate
          } else {
            rates[item.line] = item.rate
          }
        })
        params.rates = rates
      }
      return params
    },
    async handleConfirm () {
      this.loading = true
      try {
        await validateModelForm(this.$refs.form)
        const params = this.genParams()
        this.loading = true
        await this.doCreate(params)
        this.loading = false
        this.params.refresh()
        this.cancelDialog()
      } catch (error) {
        this.loading = false
      }
    },
  },
}
</script>
